import React from "react";
import { omit, capitalize } from "lodash-es";
import { useFetcher, Link as RemixLink, useLocation } from "@remix-run/react";
import { format } from "date-fns";

import { useForm, useWatch } from "react-hook-form";
import {
  Text,
  Box,
  VStack,
  Link,
  Button,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { FaGift as GiftIcon } from "react-icons/fa";

import {
  AvailabilityRequestLimitModal,
  NewFeatureBadge,
} from "#app/components/index.jsx";

import {
  List,
  ListItem,
  ListItemHeader,
  ListItemContent,
} from "#app/components/layouts/FormList.jsx";

import {
  AvailabilityRequestFormFreeAccessAlert,
  AvailabilityRequestFormTrialAlert,
  FormChanceAlert,
} from "#app/components/alerts/index.jsx";
import {
  useOptionalUser,
  useUserSubscriptionValue,
  useServerFieldErrors,
} from "#app/hooks/index.js";
import {
  includeNightsText,
  getCapacityUnit,
} from "#app/utils/availabilityRequest.js";

import { SpecificDatesInput } from "./SpecificDatesInput.jsx";
import { RequestLimitBanner } from "./RequestLimitBanner.jsx";
import { StayLengthInput } from "./StayLengthInput.jsx";
import { SiteTypeInput } from "./SiteTypeInput.jsx";
import { MaxRVLengthInput } from "./MaxRVLengthInput.jsx";
import { SiteFiltersInput } from "./SiteFiltersInput.jsx";
import { DoubleSiteInput } from "./DoubleSiteInput.jsx";
import { AccessibilityInput } from "./AccessibilityInput.jsx";
import { MaxElectricInput } from "./MaxElectricInput.jsx";
import { SelectedSitesInput } from "./SelectedSitesInput.jsx";
import { EmailInput } from "./EmailInput.jsx";
import { MinCapacityInput } from "./MinCapacityInput.jsx";
import { ParkMapAlert } from "./ParkMapAlert.jsx";
import { TripBuilder } from "./TripBuilder.jsx";
import { TripDatesSummary } from "./TripDatesSummary.jsx";
import { StaySummary } from "./StaySummary.jsx";
import { TripPlannerNudge } from "./TripPlannerNudge.jsx";
import { PartialMatchToggle } from "./PartialMatchToggle.jsx";
import {
  getActiveOfferForFacility,
  getOfferCopy,
} from "#app/config/freeAccessOffers.js";
import { getMaxPartySize } from "#app/config/partySize.js";
import { capturePostHog } from "#app/utils/posthog.client.js";
import { errors as errorMessages } from "#app/utils/constants/index.js";
import { getRequestMaxDate } from "#app/config/bookingWindow.js";
import { MAX_SELECTABLE_DATES } from "#app/config/requests.js";
import { getNightLabels } from "#app/utils/tripPlannerForm.js";

export function RequestForm({
  mode = "create",
  defaultValues,
  atRequestLimit = false,
  facility,
  siteTypes,
  siteFilters,
  maxRVLengthFilters,
  maxElectricFilters,
  tripPlannerPresets,
  tripPlannerParent,
  renderSubmit,
}) {
  const user = useOptionalUser();
  const location = useLocation();
  const maxPartySize = getMaxPartySize(facility?.agency?.scanner);
  const freeAccessOffer = getActiveOfferForFacility(facility);
  const freeAccessCopy = getOfferCopy(freeAccessOffer);
  const stickyShowsSponsor = useUserSubscriptionValue({
    none: () => false,
    trial: () => !!freeAccessOffer,
    active: () => false,
  });
  const showFreeAccessIntro =
    freeAccessOffer && mode !== "edit" && !stickyShowsSponsor;

  // This can probably be useSubmit instead since it redirects
  const fetcher = useFetcher();

  const formContext = useForm({
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = formContext;

  useServerFieldErrors(fetcher, formContext);

  const isTripPlanner = defaultValues.isTripPlanner;

  // Properties shared by every request-form funnel event. This form is the main
  // interaction on the site; `mode` lets us isolate the create funnel from
  // edits and clones.
  const formAnalyticsProps = {
    facility_slug: facility?.slug,
    facility_name: facility?.name,
    agency: facility?.agency?.scanner,
    mode,
    is_trip_planner: isTripPlanner,
  };

  const tripNights = useWatch({
    control: formContext.control,
    name: "tripNights",
  });
  const isMultiNightTrip = isTripPlanner && (tripNights?.length || 0) > 1;
  // Where the agency only sells the full itinerary, a single night can never
  // be booked, so there is nothing to choose: every request is whole-trip.
  const wholeTripOnly = isTripPlanner && !!tripPlannerPresets?.wholeTripOnly;
  const showPartialMatch = isMultiNightTrip && !wholeTripOnly;

  // With one night, or a whole-trip-only route, partial vs exact doesn't
  // matter — default to false
  React.useEffect(() => {
    if (isTripPlanner && !showPartialMatch) {
      formContext.setValue("allowPartialMatch", false, {
        shouldValidate: true,
      });
    }
  }, [isTripPlanner, showPartialMatch, formContext]);

  const allowPartialMatch = useWatch({
    control: formContext.control,
    name: "allowPartialMatch",
  });

  const onSubmit = (data) => {
    capturePostHog("request_form_submitted", formAnalyticsProps);

    const attribution = window.SchnerpAttribution || {};

    const serverData = {
      ...omit(data, ["selectedDates", "filteredSiteIds"]),
      selectedDates: data.selectedDates.map((date) =>
        format(date, "yyyy-MM-dd"),
      ),
      referralId: window.Rewardful?.referral,
      couponId: window.Rewardful?.coupon?.id,
      utmSource: attribution.utmSource,
      utmMedium: attribution.utmMedium,
      utmCampaign: attribution.utmCampaign,
      utmContent: attribution.utmContent,
      referrer: attribution.referrer,
      firstSeenAt: attribution.firstSeenAt,
    };

    if (isTripPlanner) {
      serverData.tripNights = JSON.stringify(data.tripNights);
    }

    fetcher.submit(serverData, {
      method: "post",
      action: `${location.pathname}${location.search}`,
    });
  };

  // RHF calls this instead of onSubmit when client-side validation blocks the
  // submit — the fields that errored are the form's friction points.
  const onInvalid = (formErrors) => {
    capturePostHog("request_form_validation_failed", {
      ...formAnalyticsProps,
      fields: Object.keys(formErrors),
    });

    // The trip route has no single input for RHF to focus, so when it is the
    // only thing blocking the submit, bring its first empty night into view.
    if (Object.keys(formErrors).every((field) => field === "tripNights")) {
      document
        .querySelector("[data-trip-night-empty]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Server errors describe the previous attempt, so each submit starts without
  // them. RHF re-validates registered inputs on submit, but an error the server
  // set on a field no input registers (the trip route's "server" message, or
  // selectedSiteIds in trip-planner mode) would otherwise survive and block
  // every retry until the page reloads.
  const submitForm = (event) => {
    const serverErrorFields = Object.entries(formContext.formState.errors)
      .filter(([, error]) => error?.type === "server")
      .map(([field]) => field);
    if (serverErrorFields.length > 0) {
      formContext.clearErrors(serverErrorFields);
    }
    return handleSubmit(onSubmit, onInvalid)(event);
  };

  // Funnel denominator: form viewed, with context $pageview lacks.
  React.useEffect(() => {
    capturePostHog("request_form_viewed", formAnalyticsProps);
  }, []);

  const limitModalDisclosure = useDisclosure();
  const showLimitBanner = atRequestLimit && mode !== "edit";

  // The completed-but-blocked leak: the user filled everything out, hit submit,
  // and the trial/plan request cap stopped them — the same condition the limit
  // modal opens on.
  React.useEffect(() => {
    if (
      fetcher.state === "idle" &&
      fetcher.data?.error === errorMessages.AVAILABILITY_REQUEST_LIMIT_REACHED
    ) {
      capturePostHog("request_form_limit_reached", formAnalyticsProps);
    }
  }, [fetcher.state, fetcher.data]);

  // Section drop-off map: fire once per section the first time the user focuses
  // or taps anything inside it. Taps count as well as focus because Safari and
  // iOS don't focus a button when it is tapped, and calendar days are buttons.
  // Every section ListItem carries a stable data-testid="request-form-*", so
  // one delegated handler on the form attributes any interaction to its
  // section without touching the sub-inputs.
  const engagedSectionsRef = React.useRef(null);
  if (engagedSectionsRef.current === null) {
    engagedSectionsRef.current = new Set();
  }
  const handleSectionEngage = (event) => {
    const sectionEl = event.target.closest?.("[data-testid^='request-form-']");
    if (!sectionEl) return;
    const section = sectionEl
      .getAttribute("data-testid")
      .replace("request-form-", "");
    if (engagedSectionsRef.current.has(section)) return;
    engagedSectionsRef.current.add(section);
    capturePostHog("request_form_section_engaged", {
      ...formAnalyticsProps,
      section,
    });
  };

  const StickyNotification = useUserSubscriptionValue({
    none: () => (
      <FormChanceAlert facilityId={facility.id} formContext={formContext} />
    ),
    trial: (user) => {
      if (freeAccessOffer) {
        return (
          <Box bg="bg-surface">
            <AvailabilityRequestFormFreeAccessAlert offer={freeAccessOffer} />
          </Box>
        );
      }

      if (user.subscription.trialNotificationsLeft > 0) {
        return (
          <FormChanceAlert facilityId={facility.id} formContext={formContext} />
        );
      }
      return (
        <Box bg="bg-surface">
          <AvailabilityRequestFormTrialAlert mode={mode} />
        </Box>
      );
    },
    active: () => (
      <FormChanceAlert facilityId={facility.id} formContext={formContext} />
    ),
  });

  const NotificationHeader = useUserSubscriptionValue({
    none: () => "Enter your email address",
    trial: () => "Review your email address",
    active: () => "Review notification preferences",
  });

  // TODO: Figure out what to do with this helper text
  // I don't like the "Reservable: Dec 31, 1968 - Dec 30, 9999" for Year-round
  // Removing this will simplify the UI to not have any facility reservation dates

  // const DatePickerHelperText =
  // new Date() > new Date(facility.reservationEndDate) ? (
  //   <Text as="span" color="warning">
  //     {`Reservations closed on ${format(
  //       parseISO(facility.reservationEndDate),
  //       "PP"
  //     )}. See ${facility.agency.name} for details`}
  //   </Text>
  // ) : (
  //   <Text as="span" color="inherit">
  //     {`Reservable: ${format(
  //       parseISO(facility.reservationStartDate),
  //       "PP"
  //     )} -  ${format(parseISO(facility.reservationEndDate), "PP")}`}
  //   </Text>
  // );

  const [siteType] = getValues(["siteType"]);
  const shouldIncludeNights = includeNightsText({
    siteType,
  });

  const shouldIncludeCapacity = facility.sites.some((site) => site.hasCapacity);
  const capacityUnit = shouldIncludeCapacity ? getCapacityUnit(facility) : null;

  const restrictedPeriods = React.useMemo(() => {
    const seen = new Set();
    const periods = [];
    for (const site of facility.sites) {
      for (const p of site.extraAttributes?.restrictedPeriods || []) {
        const key = `${p.start}-${p.end}`;
        if (!seen.has(key)) {
          seen.add(key);
          periods.push(p);
        }
      }
    }
    return periods;
  }, [facility.sites]);

  let step = 0;

  // Called inline so its step number follows the section order: after the
  // route in trip-planner mode, first otherwise.
  const renderDatesSection = () => (
    <ListItem data-testid="request-form-dates">
      <ListItemHeader
        title={
          isTripPlanner ? (
            `${++step}. ${mode === "edit" ? "Edit your start dates" : "When could you start?"}`
          ) : (
            <>
              {++step}. {mode === "edit" ? "Edit" : "Select"} your{" "}
              <Text as="strong">arrival dates</Text>
            </>
          )
        }
        subtitle={
          isTripPlanner ? (
            <>
              Pick every day you could start the trip. From each one, we check{" "}
              {(tripNights?.length || 0) > 1
                ? `all ${tripNights.length} nights of the trip`
                : "that night"}
              .
            </>
          ) : shouldIncludeNights ? (
            <>
              These are days you want to START your stay. If you want a weekend,
              pick only the Friday.
            </>
          ) : (
            <></>
          )
        }
      />
      <ListItemContent>
        <SpecificDatesInput
          name="selectedDates"
          formContext={formContext}
          max={MAX_SELECTABLE_DATES}
          maxDate={getRequestMaxDate(facility.slug)}
          restrictedPeriods={restrictedPeriods}
          tripNightCount={isTripPlanner ? tripNights?.length || 0 : undefined}
        />
        {isTripPlanner && (
          <TripDatesSummary
            formContext={formContext}
            facility={facility}
            analyticsProps={formAnalyticsProps}
          />
        )}
      </ListItemContent>
    </ListItem>
  );

  const tripAlertScope = !isMultiNightTrip
    ? "a spot"
    : allowPartialMatch
      ? "any night of it"
      : "the whole trip";

  return (
    <>
      <AvailabilityRequestLimitModal
        actionText="create"
        fetcher={fetcher}
        disclosure={limitModalDisclosure}
      />
      {showLimitBanner && (
        <RequestLimitBanner
          analyticsProps={formAnalyticsProps}
          onOpenLimitModal={limitModalDisclosure.onOpen}
        />
      )}
      {showFreeAccessIntro && (
        <HStack
          align="flex-start"
          spacing={3}
          px={4}
          py={3}
          mb={4}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border"
          bg="bg-surface"
          data-testid="request-form-free-access-intro"
        >
          <Box
            as={GiftIcon}
            boxSize="22px"
            color="warning"
            mt="2px"
            flexShrink={0}
            aria-hidden="true"
          />
          <Box flexGrow={1}>
            <Text fontSize="sm" fontWeight="semibold">
              {freeAccessCopy.headline}
            </Text>
            <Text fontSize="sm" color="muted">
              {freeAccessCopy.reason}
            </Text>
          </Box>
        </HStack>
      )}
      <form
        onSubmit={submitForm}
        onFocusCapture={handleSectionEngage}
        onPointerDownCapture={handleSectionEngage}
        noValidate
        translate="no"
      >
        <input type="hidden" value={facility.id} {...register("facilityId")} />
        {isTripPlanner && mode !== "edit" && (
          <Box
            px={4}
            py={3}
            mb={4}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border"
            bg="bg-surface"
            data-testid="trip-planner-intro"
          >
            <Text fontSize="sm">
              Pick your route, then the days you could start. We'll alert you
              when {tripAlertScope} opens.
            </Text>
          </Box>
        )}
        <List>
          {isTripPlanner ? (
            <>
              {/* The route comes first: until the trip's length is known, a
                  start date means nothing. The alert choice sits inside the
                  route step so section numbers don't jump as nights are
                  added or removed. */}
              <ListItem data-testid="request-form-trip-builder">
                <ListItemHeader
                  title={`${++step}. ${mode === "edit" ? "Edit" : "Choose"} your route`}
                  subtitle="Where you'll stay each night."
                />
                <ListItemContent>
                  <TripBuilder
                    formContext={formContext}
                    facility={facility}
                    presets={tripPlannerPresets}
                    mode={mode}
                  />
                  {showPartialMatch && (
                    <Box
                      mt={6}
                      pt={5}
                      borderTopWidth="1px"
                      borderColor="border"
                      data-testid="request-form-partial-match"
                    >
                      <PartialMatchToggle
                        formContext={formContext}
                        nightLabels={getNightLabels(tripNights, facility.sites)}
                      />
                    </Box>
                  )}
                </ListItemContent>
              </ListItem>

              {renderDatesSection()}

              {shouldIncludeCapacity && (
                <ListItem data-testid="request-form-capacity">
                  <ListItemHeader
                    title={`${++step}. ${mode === "edit" ? "Edit" : "Select"} your ${capacityUnit === "tent_pads" ? "tent pads" : capacityUnit === "sites" ? "site count" : capacityUnit === "slots" ? "quantity" : "party size"}`}
                    subtitle={
                      capacityUnit === "tent_pads"
                        ? "We'll only notify you of sites with enough tent pads."
                        : capacityUnit === "sites"
                          ? "We'll only notify you when enough sites are available."
                          : capacityUnit === "slots"
                            ? "We'll only notify you when enough slots are available."
                            : "We'll only notify you of trips that can fit your whole party."
                    }
                  />
                  <ListItemContent>
                    <MinCapacityInput
                      name="minCapacity"
                      facility={facility}
                      formContext={formContext}
                      max={maxPartySize}
                    />
                  </ListItemContent>
                </ListItem>
              )}
            </>
          ) : (
            <>
              {renderDatesSection()}

              {shouldIncludeNights && (
                <ListItem data-testid="request-form-nights">
                  <ListItemHeader
                    title={`${++step}. ${mode === "edit" ? "Edit" : "Select"} your nights`}
                    subtitle="Fewer nights means more notifications."
                  />
                  <ListItemContent>
                    <StayLengthInput
                      name="minStayLength"
                      formContext={formContext}
                    />
                    <StaySummary
                      formContext={formContext}
                      analyticsProps={formAnalyticsProps}
                    />
                  </ListItemContent>
                </ListItem>
              )}

              {shouldIncludeCapacity && (
                <ListItem data-testid="request-form-capacity">
                  <ListItemHeader
                    title={`${++step}. ${mode === "edit" ? "Edit" : "Select"} your ${capacityUnit === "tent_pads" ? "tent pads" : capacityUnit === "sites" ? "site count" : capacityUnit === "slots" ? "quantity" : "party size"}`}
                    subtitle={
                      capacityUnit === "tent_pads"
                        ? "We'll only notify you of sites with enough tent pads."
                        : capacityUnit === "sites"
                          ? "We'll only notify you when enough sites are available."
                          : capacityUnit === "slots"
                            ? "We'll only notify you when enough slots are available."
                            : "We'll only notify you of sites that can fit your whole party."
                    }
                  />
                  <ListItemContent>
                    <MinCapacityInput
                      name="minCapacity"
                      facility={facility}
                      formContext={formContext}
                      max={maxPartySize}
                    />
                  </ListItemContent>
                </ListItem>
              )}

              {tripPlannerParent ? (
                <ListItem data-testid="request-form-trip-nudge">
                  <ListItemHeader
                    title={`${++step}. Part of a longer trip?`}
                    subtitle={`Optional. Skip it if you're only staying at ${facility.sites[0]?.name || facility.name}.`}
                  />
                  <ListItemContent>
                    <TripPlannerNudge
                      facility={facility}
                      tripPlannerParent={tripPlannerParent}
                      formContext={formContext}
                    />
                  </ListItemContent>
                </ListItem>
              ) : (
                <ListItem data-testid="request-form-sites">
                  <ListItemHeader
                    title={`${++step}. ${mode === "edit" ? "Edit" : "Select"} your preferred sites`}
                    subtitle="Filter by amenities, useful for larger campgrounds."
                  />
                  <ListItemContent>
                    <ParkMapAlert facility={facility} siteType={siteType} />
                    <SiteTypeInput
                      name="siteType"
                      siteTypes={siteTypes}
                      formContext={formContext}
                      mb={4}
                    />
                    <MaxRVLengthInput
                      name="maxRVLength"
                      maxRVLengthFilters={maxRVLengthFilters}
                      formContext={formContext}
                      mb={4}
                    />
                    <SiteFiltersInput
                      name="selectedFilters"
                      siteFilters={siteFilters}
                      formContext={formContext}
                      mb={4}
                    />
                    <MaxElectricInput
                      name="maxElectric"
                      maxElectricFilters={maxElectricFilters}
                      formContext={formContext}
                      mb={4}
                    />
                    <DoubleSiteInput
                      name="doubleSitePreference"
                      facility={facility}
                      formContext={formContext}
                      mb={4}
                    />
                    <AccessibilityInput
                      name="accessibilityPreference"
                      facility={facility}
                      formContext={formContext}
                      mb={4}
                    />
                    <SelectedSitesInput
                      name="selectedSiteIds"
                      facility={facility}
                      formContext={formContext}
                    />
                  </ListItemContent>
                </ListItem>
              )}
            </>
          )}

          <ListItem
            direction="row"
            justifyContent="center"
            width="100%"
            position="sticky"
            top="auto"
            bottom="0"
            zIndex={2}
            data-testid="request-form-notification"
          >
            <ListItemContent Component={StickyNotification} p={0} w="100%" />
          </ListItem>

          <ListItem data-testid="request-form-email">
            <ListItemHeader
              title={`${++step}. ${NotificationHeader}`}
              subtitle="We'll notify you when we find an opening."
            ></ListItemHeader>
            <ListItemContent>
              <EmailInput name="email" formContext={formContext} />
            </ListItemContent>
          </ListItem>
        </List>

        <VStack spacing={2} mt={8} mx="auto">
          {renderSubmit(fetcher, formContext)}
          <BelowSubmitComponent
            errors={errors}
            mode={mode}
            user={user}
            isTripPlanner={isTripPlanner}
          />
        </VStack>
      </form>
    </>
  );
}

function BelowSubmitComponent({ errors, mode, user, isTripPlanner }) {
  if (Object.keys(errors).length > 0) {
    // Server errors on fields with no input on this form show here, since
    // nothing above would. In trip-planner mode the route builder owns the
    // sites, so selectedSiteIds has no input of its own.
    const unplacedError =
      errors.server || (isTripPlanner ? errors.selectedSiteIds : undefined);
    if (unplacedError) {
      return (
        <Text fontSize="sm" color="error">
          {unplacedError.message}
        </Text>
      );
    }

    return (
      <Text fontSize="sm" color="error-on-canvas">
        We can't submit yet, check the messages above.
      </Text>
    );
  }

  if (mode === "create" && !user) {
    return (
      <Text color="subtle" fontSize="sm">
        By submitting this request, you agree to our{" "}
        <Link as={RemixLink} to="/legal/terms">
          Terms and Conditions
        </Link>
        <Text as="span"> and </Text>
        <Link as={RemixLink} to="/legal/privacy">
          Privacy Policy
        </Link>
      </Text>
    );
  }

  return null;
}
